import { useContext, useMemo } from 'preact/hooks';
import { AdParameters, Asset } from '../../types';
import { PreviewContext } from '../contexts/previewContext';
import { useAdParameters } from './useAdParameters';
import { Platform, usePlatform } from './usePlatform';

const SearchRegex = /\?.*$/;

function getSearch(asset: string) {
  const searchMatch = asset && asset.match(SearchRegex);
  return searchMatch ? searchMatch[0] : '';
}

function assetUri(
  assets: { [key: string]: { high?: string; low?: string } },
  asset: string | undefined,
  platform: Platform
): string | undefined {
  if (!asset) {
    return;
  }
  if (asset.startsWith('data:')) {
    return asset;
  }

  if (platform === Platform.Unknown) {
    return;
  }

  const search = getSearch(asset);
  asset = asset.replace(SearchRegex, '');

  if (!assets[asset]) {
    return asset + search;
  }

  const low = assets[asset].low;
  const high = assets[asset].high;

  const optimizedAsset = platform === Platform.Mobile ? low || high || asset : high || asset;

  return optimizedAsset + search;
}

export function getOptimizedAsset<TConfig>(
  preview: boolean,
  ctx: AdParameters<TConfig>,
  asset: Asset | undefined,
  platform: Platform
) {
  const prefix = preview ? ctx.env.asset_bucket_uri : ctx.env.asset_cdn_uri;
  const baseUri = asset?.uri?.startsWith('data:') ? '' : prefix ? `${prefix}/` : '';

  const cacheBuster = asset?.cacheBuster ?? asset?.time;
  const query = cacheBuster ? `?${cacheBuster}` : '';

  const optimizedAsset = assetUri(ctx.assets, asset?.uri, platform);
  return optimizedAsset ? `${baseUri}${optimizedAsset}${query}` : '';
}

export function useOptimizedAsset<TConfig>(asset: Asset | undefined) {
  const platform = usePlatform();
  const ctx = useAdParameters<TConfig>();
  const preview = useContext(PreviewContext);
  return useMemo(() => {
    return getOptimizedAsset(preview, ctx, asset, platform);
  }, [asset, ctx, preview, platform]);
}
